import { useState, useEffect } from "react";
import { Download, X, Share } from "lucide-react";

export default function InstallBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed or already installed
    if (localStorage.getItem("untamed_install_dismissed")) return;
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Android/Chrome: capture the beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS: show after 3s if mobile Safari
    if (ios && navigator.standalone === undefined || (ios && !navigator.standalone)) {
      const timer = setTimeout(() => setShow(true), 3000);
      return () => { clearTimeout(timer); window.removeEventListener("beforeinstallprompt", handler); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        setShow(false);
        localStorage.setItem("untamed_install_dismissed", "1");
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSGuide(true);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem("untamed_install_dismissed", "1");
  };

  if (!show) return null;

  return (
    <>
      <div data-testid="install-banner" className="fixed bottom-0 left-0 right-0 z-50 bg-[#171717] border-t border-[#F7931E]/40 px-4 py-3 safe-area-pb animate-slide-up">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-[#F7931E] flex items-center justify-center flex-shrink-0">
            <Download size={20} className="text-black" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm leading-tight">Get the Untamed App</p>
            <p className="text-[#A3A3A3] text-xs leading-tight">Add to home screen for quick access</p>
          </div>
          <button
            data-testid="install-btn"
            onClick={handleInstall}
            className="bg-[#F7931E] text-black font-bold uppercase tracking-wider text-xs px-4 py-2 hover:bg-[#D97715] transition-colors flex-shrink-0">
            Install
          </button>
          <button
            data-testid="install-dismiss-btn"
            onClick={handleDismiss}
            className="text-[#525252] hover:text-white transition-colors flex-shrink-0 p-1">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-end justify-center p-4" onClick={() => setShowIOSGuide(false)}>
          <div data-testid="ios-install-guide" className="bg-[#171717] border border-[#262626] p-6 max-w-md w-full mb-safe" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold uppercase tracking-tight text-lg font-heading mb-4">Install on iPhone</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-[#F7931E] text-black font-bold flex items-center justify-center text-sm flex-shrink-0">1</div>
                <div>
                  <p className="text-white text-sm font-bold">Tap the Share button</p>
                  <p className="text-[#A3A3A3] text-xs flex items-center gap-1">Look for <Share size={14} className="text-[#F7931E]" /> at the bottom of Safari</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-[#F7931E] text-black font-bold flex items-center justify-center text-sm flex-shrink-0">2</div>
                <div>
                  <p className="text-white text-sm font-bold">Scroll down & tap "Add to Home Screen"</p>
                  <p className="text-[#A3A3A3] text-xs">It may be below the first row of options</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-[#F7931E] text-black font-bold flex items-center justify-center text-sm flex-shrink-0">3</div>
                <div>
                  <p className="text-white text-sm font-bold">Tap "Add"</p>
                  <p className="text-[#A3A3A3] text-xs">The Untamed Gear icon appears on your home screen</p>
                </div>
              </div>
            </div>
            <button onClick={() => setShowIOSGuide(false)}
              className="w-full mt-6 bg-[#F7931E] text-black font-bold uppercase tracking-wide py-3 text-sm hover:bg-[#D97715] transition-colors">
              Got It
            </button>
          </div>
        </div>
      )}
    </>
  );
}
